import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { cancelRun, getRun, KEEPALIVE, listProjects } from '../lifeos/api.js';
import { logLine, runTone, taskTypeLabel, TERMINAL } from '../lifeos/agents.js';
import { humanize, levelLabel, runCode, stamp } from '../lifeos/format.js';
import { useToast } from '../lifeos/toast.js';
import Structured from '../lifeos/Structured.js';
import { Collapsible, Loader, SafeLink } from '../lifeos/ui.js';
import { Button, ButtonLink, Group, Page, Row } from '../ui/index.js';
import { useLoad } from '../lifeos/useLoad.js';
import type { AgentRunRow } from '../lifeos/types.js';

/**
 * One agent run (`/agents/runs/:id`): status, the request it came from (third-
 * party text, shown as a quote), the plan, the review and the log. The run
 * moves only through approvals; the one action here is Cancel.
 */
export default function AgentRun(): JSX.Element {
  const { id = '' } = useParams();
  const run = useLoad(() => getRun(id), [id]);
  const projects = useLoad(() => listProjects(), []);
  const toast = useToast();
  const [cancelling, setCancelling] = useState(false);

  function cancel(r: AgentRunRow): void {
    setCancelling(true);
    toast.defer({
      text: `Cancelled ${runCode(r.shortCode)}`,
      commit: () => cancelRun(r.id, KEEPALIVE),
      onUndo: () => setCancelling(false),
      onDone: () => void run.reload().finally(() => setCancelling(false)),
      onError: () => setCancelling(false),
      failText: `Couldn't cancel ${runCode(r.shortCode)}`,
    });
  }

  const back = { to: '/agents?tab=runs', label: 'Runs' };
  return (
    <Loader state={run}>
      {(r) => {
        const project = projects.data?.find((p) => p.id === r.projectId);
        const open = !TERMINAL.has(r.status) && !cancelling;
        const tone = runTone(r.status);
        return (
          <Page
            title={`${runCode(r.shortCode)} · ${taskTypeLabel(r.taskType)}`}
            back={back}
            className="bc-run-page"
            sub={
              <span className="bc-runsub">
                <span className={`bc-status bc-status--${tone === 'accent' ? 'run' : tone === 'muted' ? 'idle' : tone}`} aria-hidden="true" />
                {humanize(r.status)}
                {r.dryRun && ' · dry run'}
                {project && (
                  <>
                    {' · '}
                    <Link to={`/projects/${project.id}`}>{project.name}</Link>
                  </>
                )}
              </span>
            }
          >
            {r.error && (
              <p className="lo-hardstop bc-banner" role="note">
                {r.error}
              </p>
            )}
            {(r.approvalId || open) && (
              <div className="bc-actions">
                {r.approvalId && (
                  <ButtonLink size="lg" to={`/agents?approval=${encodeURIComponent(r.approvalId)}`}>
                    Open approval
                  </ButtonLink>
                )}
                {open && (
                  <Button variant="destructive" size="lg" onClick={() => cancel(r)}>
                    Cancel run
                  </Button>
                )}
              </div>
            )}

            <Group header="Details" label="Status">
              {r.risk && (
                <Row
                  title="Risk"
                  detail={<span className={r.risk === 'high' ? 'bc-bad' : r.risk === 'medium' ? 'bc-warn' : undefined}>{r.risk === 'high' ? 'High' : r.risk === 'medium' ? 'Medium' : 'Low'}</span>}
                />
              )}
              {r.levelAtStart !== null && <Row title="Level" detail={levelLabel(r.levelAtStart)} />}
              {r.branch && <Row title="Branch" detail={<span className="lo-code bc-mono">{r.branch}</span>} />}
              {r.commitSha && <Row title="Commit" detail={<span className="lo-code bc-mono">{r.commitSha.slice(0, 10)}</span>} />}
              {r.diffStat && <Row title="Diff" sub={<span className="lo-code bc-mono">{r.diffStat}</span>} />}
              {r.stagingUrl && <Row title="Staging" detail={<SafeLink href={r.stagingUrl} />} />}
              <Row title="Started" detail={stamp(r.createdAt)} />
              {r.heartbeatAt && <Row title="Last heartbeat" detail={stamp(r.heartbeatAt)} />}
              {r.finishedAt && <Row title="Finished" detail={stamp(r.finishedAt)} />}
            </Group>

            <section className="ui-groupwrap" aria-label="Request">
              <div className="ui-group__head">
                <h2 className="ui-group__title">Request</h2>
              </div>
              <blockquote className="lo-untrusted bc-quote">
                <span className="lo-untrusted__label">
                  {r.request.from_handle ? `From ${r.request.from_handle}` : 'Started by you'}
                  {!r.request.sender_verified && ' · unverified sender'}
                </span>
                {r.request.untrusted_text}
              </blockquote>
            </section>

            {r.plan !== null && r.plan !== undefined && (
              <section className="ui-groupwrap bc-structured" aria-label="Plan">
                <div className="ui-group__head">
                  <h2 className="ui-group__title">Plan</h2>
                </div>
                <Structured value={r.plan} />
              </section>
            )}
            {r.review !== null && r.review !== undefined && (
              <section className="ui-groupwrap bc-structured" aria-label="Review">
                <div className="ui-group__head">
                  <h2 className="ui-group__title">Review</h2>
                </div>
                <Structured value={r.review} />
              </section>
            )}

            <section className="ui-groupwrap" aria-label="Log">
              <div className="ui-group__head">
                <h2 className="ui-group__title">
                  Log <span className="bc-count">{r.log.length}</span>
                </h2>
              </div>
              {r.log.length === 0 ? (
                <p className="ui-group__foot">Nothing logged yet.</p>
              ) : (
                <ol className="lo-log bc-log">
                  <Collapsible
                    items={[...r.log].reverse()}
                    n={8}
                    noun="older"
                    render={(entry, i) => {
                      const l = logLine(entry);
                      return (
                        <li key={i} className="lo-log__row">
                          <span className="lo-log__text">{l.text}</span>
                          {l.at && <span className="lo-log__at">{stamp(l.at)}</span>}
                        </li>
                      );
                    }}
                  />
                </ol>
              )}
            </section>
          </Page>
        );
      }}
    </Loader>
  );
}
